import {
    Paragraph,
    TextRun,
    Table,
    Header,
    PageNumber,
    NumberFormat,
    HeadingLevel,
    AlignmentType,
    SectionType,
    WidthType,
    PageOrientation
} from "docx";
import * as m from "@app/paraglide/messages.js";
import tcLib from '@cclib/lib/timecode.js';

/**
 * Builds the title/heading section displayed above the table.
 * Used by: default, technicolor
 * @param {string} documentTitle - The document title
 * @returns {object} docx section definition
 */
export function buildHeadSection(documentTitle) {
    return {
        properties: {
            type: SectionType.CONTINUOUS,
            page: {
                size: {
                    orientation: PageOrientation.LANDSCAPE,
                },
            }
        },
        children: [
            new Paragraph({
                children: [new TextRun({ text: documentTitle })],
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
                thematicBreak: true
            }),
            new Paragraph({
                children: [new TextRun({
                    text: m.ccsl_export_heading_combined_continuity_spotting()
                })],
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
                thematicBreak: true
            })
        ]
    };
}

/**
 * Builds the metadata section showing author, frame rate, drop frame, and incode.
 * Used by: default, technicolor
 * @param {object} project - Object with { username, frameRate, dropFrame, incode }
 * @returns {object} docx section definition
 */
export function buildMetadataSection(project) {
    return {
        properties: {
            type: SectionType.CONTINUOUS,
            page: {
                size: {
                    orientation: PageOrientation.LANDSCAPE,
                },
            }
        },
        children: [
            new Paragraph({
                spacing: { before: 300 },
                children: [new TextRun({
                    text: m.ccsl_export_metadata_author({ name: project.username }),
                    bold: true
                })]
            }),
            new Paragraph({
                children: [new TextRun({
                    text: m.ccsl_export_metadata_frame_rate({ rate: project.frameRate }),
                    bold: true
                })]
            }),
            new Paragraph({
                children: [new TextRun({
                    text: m.ccsl_export_metadata_drop_frame({
                        value: project.dropFrame
                            ? m.ccsl_export_yes()
                            : m.ccsl_export_no()
                    }),
                    bold: true
                })]
            }),
            new Paragraph({
                spacing: { after: 300 },
                children: [new TextRun({
                    text: m.ccsl_export_metadata_video_incode({
                        timecode: tcLib.secToTc(project.incode, project.frameRate, project.dropFrame)
                    }),
                    bold: true
                })]
            })
        ]
    };
}

/**
 * Builds the main section containing the data table.
 * @param {Array} tableRows - Array of TableRow objects
 * @param {object} templateConfig - Template document config from getDocumentConfig()
 * @returns {object} docx section definition
 */
export function buildMainSection(tableRows, templateConfig) {
    const section = {
        properties: {
            type: SectionType.CONTINUOUS,
            page: {
                pageNumbers: {
                    start: 1,
                    formatType: NumberFormat.DECIMAL,
                },
                size: {
                    orientation: templateConfig.orientation,
                }
            }
        },
        children: [
            new Table({
                rows: tableRows,
                width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                }
            })
        ]
    };

    if (templateConfig.hasPageHeader) {
        section.headers = {
            default: new Header({
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({
                                children: [
                                    m.ccsl_export_page_prefix(),
                                    PageNumber.CURRENT,
                                    m.ccsl_export_page_of(),
                                    PageNumber.TOTAL_PAGES
                                ],
                            })
                        ],
                    }),
                ],
            }),
        };
    }

    return section;
}

/**
 * Builds the footer section displayed below the table.
 * Used by: default, technicolor
 * @param {string} authStatus - The auth status (e.g. "in_trial")
 * @returns {object} docx section definition
 */
export function buildFooterSection(authStatus) {
    return {
        properties: {
            page: {
                size: {
                    orientation: PageOrientation.LANDSCAPE,
                },
            }
        },
        children: [
            new Paragraph({
                children: [new TextRun({
                    text: m.ccsl_export_footer_created({
                        trialSuffix: authStatus === "in_trial"
                            ? ` ${m.ccsl_export_trial_mode_suffix()}`
                            : ""
                    })
                })],
                heading: HeadingLevel.HEADING_4,
                alignment: AlignmentType.CENTER,
                thematicBreak: true
            })
        ]
    };
}

/**
 * Assembles the final array of document sections based on template configuration.
 * @param {Array} tableRows - Array of TableRow objects
 * @param {object} templateConfig - Template config from getDocumentConfig()
 * @param {string} documentTitle - The document title
 * @param {object} project - Project state object
 * @param {string} authStatus - Auth status string
 * @returns {Array} Array of docx section definitions
 */
export function assembleSections(tableRows, templateConfig, documentTitle, project, authStatus) {
    const mainSection = buildMainSection(tableRows, templateConfig);

    if (templateConfig.useSections) {
        return [
            buildHeadSection(documentTitle),
            buildMetadataSection(project),
            mainSection,
            buildFooterSection(authStatus)
        ];
    }

    return [mainSection];
}
